import { type CardStylingDataStructure } from 'javascripts/shared-types';
import CardStyling from '../CardStyling';
import {
  type CardLayoutModelConstructorType,
  type StatusableType,
} from '../types';
import { type CardStylingTypesEnum_value } from 'components/framework/models/generated/types';
import { currentFlavorSupportsTeams } from 'javascripts/util/currentFlavorSupportsTeams';
import { Task } from 'components/framework/models';

// Use to generate styling information for a card that has its background color
// change depending some status of the record (not necessarily workflow status).
export class BackgroundStatus extends CardStyling {
  static type: CardStylingTypesEnum_value = 'BackgroundStatus';
  static title: string = 'Status (background)';

  static subqueriesForModel(_modelClass: CardLayoutModelConstructorType) {
    if (_modelClass.typename === 'Task') {
      return {};
    }

    if (
      currentFlavorSupportsTeams() &&
      _modelClass.hasField('teamWorkflowStatus')
    ) {
      return { teamWorkflowStatus: ['color'] };
    }

    return { workflowStatus: ['color'] };
  }

  static attributesForModel(modelClass: CardLayoutModelConstructorType) {
    if (modelClass.typename === 'Task') {
      return ['completedDate', 'dueDate'];
    }

    return [];
  }

  // Styles to show a generic example
  exampleCardStyling() {
    return {
      highlightColor: '--aha-green-500',
      useBackgroundColor: true,
    };
  }

  cardStyling(record: StatusableType | Task): CardStylingDataStructure {
    if (record instanceof Task) {
      return this.taskCardStyling(record);
    } else {
      const status =
        currentFlavorSupportsTeams() && 'teamWorkflowStatus' in record
          ? record.teamWorkflowStatus
          : record.workflowStatus;
      if (!status) return {};
      const { colorObject } = status;
      const highlightColor = colorObject.hex();

      return { highlightColor, useBackgroundColor: true };
    }
  }

  // Tasks base their colour on the due date. This is in use by whiteboards
  // so we have to use hard coded hex values instead of theme variables.
  private taskCardStyling(record: Task): CardStylingDataStructure {
    let highlightColor: string;

    // Base the colour of the task off the due date/completion status.
    if (record.completedDate) {
      highlightColor = '#e5f3d6'; // --theme-green-background
    } else if (record.dueDate) {
      // dueDate is a string in the format 'YYYY-MM-DD'. We have to compare just
      // days normalized to local midnight.
      const today = new Date();
      const todayLocal = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );

      // Handle both string and Date types for dueDate
      let dueDate: Date;
      if (typeof record.dueDate === 'string') {
        const [year, month, day] = record.dueDate.split('-').map(Number);
        dueDate = new Date(year, month - 1, day); // local midnight
      } else {
        dueDate = record.dueDate; // It's already a Date object
      }

      if (dueDate < todayLocal) {
        highlightColor = '#fae7e1'; // overdue, --theme-red-background
      } else if (dueDate.getTime() === todayLocal.getTime()) {
        highlightColor = '#e5dced'; // due today, --theme-task-date-today-background
      } else {
        highlightColor = '#dbebff'; // upcoming, --theme-blue-background
      }
    } else {
      highlightColor = '#f8f9fa'; // no due date, --theme-tertiary-background
    }

    return { highlightColor, useBackgroundColor: true };
  }
}
